import React from 'react';
import { AppSelectDropdown } from '../AppSelectDropdown';
import {
  normalizePlantafelDayHours,
  PLANTAFEL_DEFAULT_ACTIVITY_DURATION_PRESETS,
  PLANTAFEL_WEEKDAY_LABELS,
  togglePlantafelWeekday,
  type PlantafelDayHours,
  type PlantafelVisibleWeekdays,
} from '../../utils/plantafelSettings';

const FIELD_LABEL = 'text-sm font-medium text-app-text';
const SELECT_TRIGGER = '!min-h-0 h-9 !py-0';

const DURATION_OPTIONS = PLANTAFEL_DEFAULT_ACTIVITY_DURATION_PRESETS.map((minutes) => ({
  value: String(minutes),
  label: minutes < 60 ? `${minutes} Min.` : minutes === 60 ? '1 Stunde' : `${minutes / 60} Stunden`,
}));

const HOUR_START_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${hour}:00`,
}));

const HOUR_END_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i + 1;
  return { value: String(hour), label: `${hour}:00` };
});

export function PlantafelDisplaySettingsForm({
  weekdays,
  onWeekdaysChange,
  dayHours,
  onDayHoursChange,
  defaultActivityDurationMinutes,
  onDefaultActivityDurationMinutesChange,
  disabled = false,
}: {
  weekdays: PlantafelVisibleWeekdays;
  onWeekdaysChange: (next: PlantafelVisibleWeekdays) => void;
  dayHours: PlantafelDayHours;
  onDayHoursChange: (next: PlantafelDayHours) => void;
  defaultActivityDurationMinutes: number;
  onDefaultActivityDurationMinutesChange: (next: number) => void;
  disabled?: boolean;
}) {
  const setHourStart = (raw: number) => {
    onDayHoursChange(normalizePlantafelDayHours(raw, dayHours.hourEnd));
  };
  const setHourEnd = (raw: number) => {
    onDayHoursChange(normalizePlantafelDayHours(dayHours.hourStart, raw));
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm text-app-text-muted">
          Wählen Sie die Wochentage, die in der Plantafel standardmäßig angezeigt werden.
        </p>
        <ul className="space-y-1">
          {PLANTAFEL_WEEKDAY_LABELS.map((label, weekday) => {
            const checked = weekdays.includes(weekday);
            const onlyOneLeft = checked && weekdays.length === 1;
            return (
              <li key={weekday}>
                <label
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm transition-colors ${
                    checked
                      ? 'border-emerald-200 bg-emerald-50/80 text-app-text'
                      : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                  } ${disabled || onlyOneLeft ? 'cursor-not-allowed opacity-70' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                    checked={checked}
                    disabled={disabled || onlyOneLeft}
                    onChange={() => onWeekdaysChange(togglePlantafelWeekday(weekdays, weekday))}
                  />
                  <span className="font-medium">{label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-2 border-t border-app-border pt-4">
        <p className="text-sm text-app-text-muted">
          Sichtbarer Zeitbereich in der Tages- und Wochenansicht (Termine außerhalb werden am Rand
          angezeigt).
        </p>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="block">
            <span className={`mb-1.5 block ${FIELD_LABEL}`}>Von</span>
            <AppSelectDropdown
              value={String(dayHours.hourStart)}
              onChange={(v) => setHourStart(parseInt(v, 10))}
              options={HOUR_START_OPTIONS}
              disabled={disabled}
              className="w-[7.5rem]"
              triggerClassName={SELECT_TRIGGER}
            />
          </label>
          <span className="pb-2 text-slate-400">–</span>
          <label className="block">
            <span className={`mb-1.5 block ${FIELD_LABEL}`}>Bis</span>
            <AppSelectDropdown
              value={String(dayHours.hourEnd)}
              onChange={(v) => setHourEnd(parseInt(v, 10))}
              options={HOUR_END_OPTIONS}
              disabled={disabled}
              className="w-[7.5rem]"
              triggerClassName={SELECT_TRIGGER}
            />
          </label>
        </div>
      </div>

      <div className="space-y-2 border-t border-app-border pt-4">
        <p className="text-sm text-app-text-muted">
          Standarddauer neuer Termine beim Klick in die Plantafel (Ziehen setzt die Dauer weiterhin
          manuell).
        </p>
        <label className="block">
          <span className={`mb-1.5 block ${FIELD_LABEL}`}>Standarddauer neuer Termine</span>
          <AppSelectDropdown
            value={String(defaultActivityDurationMinutes)}
            onChange={(v) => onDefaultActivityDurationMinutesChange(parseInt(v, 10))}
            options={DURATION_OPTIONS}
            disabled={disabled}
            className="w-full max-w-[12rem]"
            triggerClassName={SELECT_TRIGGER}
          />
        </label>
      </div>
    </div>
  );
}
